import { Button, Chip } from '@nextui-org/react'
import { FC, useCallback, useMemo, useState } from 'react'
import { MdFileDownload } from 'react-icons/md'
import ApiStateView from '../ApiState'
import BackButton from '../BackButton'
import LogiSearch from '../LogiSearch'
import LogiTable from '../LogiTable'
import Panel from '../Panel'
import SectionTitle from '../SectionTitle'
import { TONES } from '../../constants'
import { useLogisticBox } from '../../context/LogisticBoxContext'
import { useApi } from '../../hooks/useApi'
import { useSelectedSeller } from '../../hooks/useSelectedSeller'
import { suppliesApi, InventoryStoreRow, InventoryCell } from '../../api'
import { ToneKey } from '../../models'

const StockNum: FC<{ cell: InventoryCell | undefined }> = ({ cell }) => {
  if (cell === undefined) return <span className='text-gray-300'>—</span>
  const tone: ToneKey = cell.bajo_safety ? (cell.porcentaje < 40 ? 'rosa' : 'amber') : 'green'
  return (
    <span className='font-semibold text-xs' style={{ color: tone === 'green' ? undefined : TONES[tone].ink }}>
      {cell.cantidad_actual} <span className='text-gray-400 font-normal'>/ {cell.safety_stock}</span>
    </span>
  )
}

const fmtFecha = (iso: string | null): string => {
  if (iso === null) return '—'
  const d = new Date(iso)
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`
}

const cellOf = (row: InventoryStoreRow, codigo: string): InventoryCell | undefined =>
  row.celdas.find(c => c.codigo === codigo)

const sugerir = (cell: InventoryCell | undefined): number => {
  if (cell === undefined || !cell.bajo_safety) return 0
  return Math.max(0, Math.ceil(cell.safety_stock * 1.5) - cell.cantidad_actual)
}

const columns = [
  { name: 'Tienda', uid: 'store' },
  { name: 'Cajas', uid: 'caja', align: 'center' as const },
  { name: 'Bolsas A', uid: 'bolsa_a', align: 'center' as const },
  { name: 'Bolsas B', uid: 'bolsa_b', align: 'center' as const },
  { name: 'Sellos', uid: 'sello', align: 'center' as const },
  { name: 'Último restock', uid: 'ultimo_restock' },
  { name: 'Alerta', uid: 'alerta' },
  { name: '', uid: 'actions' }
]

const TodoInventarioView: FC = () => {
  const { openSolicitar } = useLogisticBox()
  const idSeller = useSelectedSeller()
  const [search, setSearch] = useState('')

  const data = useApi(useCallback(async () => await suppliesApi.inventoryByStore(idSeller), [idSeller]), [idSeller])

  const rows = useMemo(
    () => (data.data ?? []).filter(t => t.store.toLowerCase().includes(search.toLowerCase())),
    [data.data, search]
  )

  const renderCell = (t: InventoryStoreRow, columnKey: string): JSX.Element => {
    switch (columnKey) {
      case 'store':
        return <span className='font-medium text-xs'>{t.store}</span>
      case 'caja':
        return <StockNum cell={cellOf(t, 'caja')} />
      case 'bolsa_a':
        return <StockNum cell={cellOf(t, 'bolsa_a')} />
      case 'bolsa_b':
        return <StockNum cell={cellOf(t, 'bolsa_b')} />
      case 'sello':
        return <StockNum cell={cellOf(t, 'sello')} />
      case 'ultimo_restock':
        return <span className='text-xs text-[#151930]/60'>{fmtFecha(t.ultimo_restock_at)}</span>
      case 'alerta':
        return (
          <Chip size='sm' variant='flat' color={t.tiene_alerta ? 'danger' : 'success'}>
            {t.tiene_alerta ? 'Stock bajo' : 'OK'}
          </Chip>
        )
      case 'actions':
        return (
          <div className='flex gap-2 justify-end items-center whitespace-nowrap'>
            <Button
              size='sm'
              className='bg-black text-white text-xs h-8'
              onPress={() => openSolicitar({
                tienda: t.store,
                idStore: t.id_store,
                cajas: sugerir(cellOf(t, 'caja')),
                bolsasA: sugerir(cellOf(t, 'bolsa_a')),
                bolsasB: sugerir(cellOf(t, 'bolsa_b')),
                sellos: sugerir(cellOf(t, 'sello'))
              })}
            >
              Solicitar insumos
            </Button>
          </div>
        )
      default:
        return <span className='text-xs'>{(t as any)[columnKey]}</span>
    }
  }

  return (
    <div className='space-y-4'>
      <BackButton to='insumos' />

      <Panel>
        <div className='flex items-center justify-between mb-4 flex-wrap gap-3'>
          <SectionTitle
            title={`Inventario por tienda · ${data.data?.length ?? 0} tiendas`}
            description='Safety stock · Cajas: 2 · Bolsas A: 50 · Bolsas B: 30 · Sellos: 20'
          />
          <div className='flex gap-2 flex-wrap items-center'>
            <LogiSearch value={search} onChange={setSearch} placeholder='Buscar tienda…' className='w-56' />
            <Button variant='bordered' className='border-gray-200 text-xs' startContent={<MdFileDownload className='text-base' />}>Exportar</Button>
          </div>
        </div>

        <ApiStateView
          loading={data.loading} error={data.error} onRetry={data.reload}
        >
          <LogiTable
            columns={columns}
            items={rows}
            renderCell={renderCell}
            rowKey={(t) => t.id_store}
            ariaLabel='Inventario por tienda'
            loading={data.loading}
            emptyText='Sin inventario cargado'
          />
        </ApiStateView>
      </Panel>
    </div>
  )
}

export default TodoInventarioView
